import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Send, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const Chat = () => {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const fetch = async () => {
    try {
      const res = await client.get(`/chat/${matchId}`);
      setMessages(res.data.messages);
      const otherId = res.data.match.users.find(u => u !== user.id);
      if (otherId) {
        const profile = await client.get(`/profile/${otherId}`);
        setOther(profile.data.profile);
      }
    } catch (err) {
      toast.error("Conversa não encontrada.");
      navigate("/matches");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetch(); }, [matchId]);

  useEffect(() => {
    const t = setInterval(fetch, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const res = await client.post(`/chat/${matchId}`, { text: t });
      setMessages((m) => [...m, res.data]);
      setText("");
    } catch { toast.error("Falha ao enviar."); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] flex flex-col" data-testid="chat-page">
      <Navbar />
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 md:px-6 py-6 flex flex-col">
        <button onClick={()=>navigate("/matches")} className="text-sm text-[#A29F9B] hover:text-[#C5A880] inline-flex items-center gap-2 mb-4" data-testid="chat-back">
          <ArrowLeft className="w-4 h-4" /> Ligações
        </button>

        {other && (
          <div className="bg-[#161920] border border-white/5 rounded-2xl px-5 py-4 mb-4 flex items-center gap-4" data-testid="chat-header">
            <div className="w-12 h-12 rounded-full bg-[#C5A880] text-[#0C0E12] flex items-center justify-center font-semibold">
              {(other.name || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="font-display text-xl">{other.name}, {other.age}</h2>
              {other.location && <p className="text-xs text-[#A29F9B]">{other.location}</p>}
            </div>
          </div>
        )}

        <div className="flex-1 bg-[#161920] border border-white/5 rounded-2xl p-4 md:p-6 overflow-y-auto" style={{ minHeight: 400, maxHeight: "60vh" }} data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center text-[#A29F9B] mt-12">
              <p>Sem mensagens ainda. Sê o primeiro a quebrar o gelo.</p>
            </div>
          )}
          <div className="space-y-3">
            {messages.map((m) => {
              const mine = m.from_user === user.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                  <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    mine ? "bg-[#C5A880] text-[#0C0E12]" : "bg-[#1B1E26] text-[#F5F2EE] border border-white/5"
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</p>
                    <p className={`text-[10px] mt-1 ${mine ? "text-[#0C0E12]/60" : "text-[#A29F9B]"}`}>
                      {new Date(m.created_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        </div>

        <form onSubmit={send} className="mt-4 flex items-center gap-3">
          <input value={text} onChange={(e)=>setText(e.target.value)}
            placeholder="Escreve uma mensagem..."
            className="flex-1 bg-[#161920] border border-white/10 rounded-full px-5 py-3 text-[#F5F2EE] outline-none focus:border-[#C5A880]/50"
            data-testid="chat-input" />
          <button type="submit" disabled={sending || !text.trim()}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="chat-send">
            <Send className="w-4 h-4" /> Enviar
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;
